import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Search, ChevronDown, ChevronUp } from 'lucide-react';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import 'react-calendar/dist/Calendar.css';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';

type DateRange = '3days' | '1month' | 'custom';
type Value = [Date | null, Date | null];

export function NewDataset() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sites, setSites] = useState<{ sid: number; name: string; }[]>([]);
  const [selectedSite, setSelectedSite] = useState<number | ''>('');
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>('3days');
  const [dateValue, setDateValue] = useState<Value>([null, null]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queryDateRange, setQueryDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null
  });

  useEffect(() => {
    fetchSites();
  }, []);

  async function fetchSites() {
    try {
      const response = await fetch('/api/sites');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sites: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: ' + JSON.stringify(data));
      }

      setSites(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load sites';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate custom date range if selected
    if (dateRange === 'custom' && (!dateValue[0] || !dateValue[1])) {
      setError('Please select both start and end dates for custom date range');
      return;
    }
    
    // Validate that end date is not before start date
    if (dateRange === 'custom' && dateValue[1] && dateValue[0] && dateValue[1] < dateValue[0]) {
      setError('End date cannot be before start date');
      return;
    }
    
    // Validate that date range is not in the future
    if (dateRange === 'custom' && dateValue[1] && dateValue[1] > new Date()) {
      setError('Date range cannot be in the future');
      return;
    }
    
    // Build URL with appropriate parameters
    const params = new URLSearchParams({
      sid: String(selectedSite),
      dateRange,
      ...(dateRange === 'custom' && {
        startDate: dateValue[0]?.toISOString(),
        endDate: dateValue[1]?.toISOString()
      })
    });
    
    if (dateRange === 'custom') {
      params.append('startDate', dateValue[0]?.toISOString() || '');
      params.append('endDate', dateValue[1]?.toISOString() || '');
    }
    
    navigate(`/datasets/transactions?${params.toString()}`);
  };

  // Filter and sort sites based on search input
  const filteredSites = sites.filter(site =>
    site.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedSiteName = sites.find(site => site.sid === selectedSite)?.name;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.site-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/datasets')}
        className="flex items-center text-sm text-indigo-600 hover:text-indigo-900 mb-8"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Datasets
      </button>

      <div className="bg-white shadow-lg rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">Create New Dataset</h1>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700">
              Date Range
            </label>
            <select
              id="dateRange"
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value as DateRange);
                setDateValue([null, null]);
              }}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            >
              <option value="3days">3-6 Days Ago</option>
              <option value="1month">Last Full Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Date Range
              </label>
              <DateRangePicker
                value={dateValue}
                onChange={setDateValue as any}
                maxDate={new Date()}
                format="y-MM-dd"
                className="w-full"
                rangeDivider=" to "
                required={dateRange === 'custom'}
                clearIcon={null}
              />
            </div>
          )}

          <div className="site-selector">
            <label htmlFor="site" className="block text-sm font-medium text-gray-700">
              Select Site
            </label>
            <div className="mt-1 relative">
              <div
                className="relative"
                onClick={() => setIsOpen(!isOpen)}
              >
                <input
                  type="text"
                  id="site"
                  value={isOpen ? searchTerm : selectedSiteName || ''}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsOpen(true);
                  }}
                  onFocus={() => setIsOpen(true)}
                  placeholder="Search and select a site..."
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm pr-10"
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  {isOpen ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {isOpen && (
                <div
                  className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                >
                  {filteredSites.length === 0 ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      No sites found
                    </div>
                  ) : (
                    filteredSites.map((site) => (
                      <div
                        key={site.sid}
                        className={`relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                          selectedSite === site.sid
                            ? 'bg-indigo-600 text-white'
                            : 'text-gray-900 hover:bg-indigo-50'
                        }`}
                        onClick={() => {
                          setSelectedSite(site.sid);
                          setSearchTerm('');
                          setIsOpen(false);
                        }}
                      >
                        <span className="block truncate">{site.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {!isOpen && selectedSiteName && (
                <div className="mt-1 text-sm text-gray-500">
                  Selected site: {selectedSiteName}
                </div>
              )}

              {isOpen && (
                <div className="mt-1 text-sm text-gray-500">
                  {filteredSites.length} {filteredSites.length === 1 ? 'site' : 'sites'} found
                </div>
              )}

              {!isOpen && !selectedSiteName && (
                <div className="mt-1 text-sm text-gray-500">
                  Click to search and select a site
                </div>
              )}

              {error && (
                <div className="mt-1 text-sm text-red-600">
                  {error}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/datasets')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedSite}
              className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              Create Dataset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}